import { getCustomEyecatch } from '@/utils/eyecatch'
import { timeConverter } from '@/utils/converter'
import PageDetector from '@/utils/PageDetector'
import OnlineEventMixin from '@/utils/mixins/OnlineEventMixin'
import MjSelectMixin from '@/utils/mixins/MjSelectMixin'
import GTM from '@/utils/tracking/gtm'
import { GA4_BEGIN_CHECKOUT } from '@/utils/constants/ga4'
import { SEO_SPECIAL_PROMOTER_YORUNOYUENCHI } from '@/utils/constants/commons'
import { shouldDisablePurchaseButton } from '@/utils/purchaseValidation'
export default {
  mixins: [OnlineEventMixin, MjSelectMixin],
  data() {
    return {
      loadedLatestTicketInfo: false
    }
  },
  computed: {
    pageDetector() {
      return new PageDetector(this.$route)
    },
    isAdvertisement() {
      if (!this.event) return
      return this.event.advertisement
    },
    customEyecatch() {
      if (!this.event) return
      return getCustomEyecatch(this.event.id, this.$store.$state.my_profile, this.$store.myGender)
    },
    startAt() {
      return timeConverter(this.event.start_at)
    },
    endAt() {
      return timeConverter(this.event.end_at)
    },
    isBarGourmetEvent() {
      return this.event.type === 'bar_gourmet'
    },
    eventCode() {
      return this.$route.params.code
    },
    eventTitle() {
      if (
        this.isOnlineEvent ||
        this.event.prefecture_slug === 'other' ||
        this.event.area_slug.includes('other') ||
        this.event.organizer_promoter_event_slug === SEO_SPECIAL_PROMOTER_YORUNOYUENCHI
      ) {
        return `${this.event.title}`
      }
      return `${this.event.title}（${this.event.prefecture_name}/${this.event.area_name}）`
    },
    onDuringDayEvent() {
      return this.startAt.fullDate === this.endAt.fullDate
    },
    filteredTicketInfo() {
      if (!this.ticketInfo) return null
      return { ...this.ticketInfo, tickets: this.ticketInfo.tickets }
    },
    disablePurchaseButton() {
      return (
        this.ticketInfo &&
        (!this.ticketInfo.event_can_buy ||
          this.ticketInfo.already_bought_with_konkatsu_freepass ||
          this.hidePurchaseButton ||
          (this.abGroup && this.abGroup === 'B' && this.allTicketsSoldOut))
      )
    },
    lotteryDeadlineAt() {
      if (!this.filteredTicketInfo.lottery_deadline_at) return null
      return timeConverter(this.filteredTicketInfo.lottery_deadline_at).shortDateWithWeekDayAndTime
    },
    lotteryResultTimeAt() {
      if (!this.filteredTicketInfo.lottery_result_time_at) return null
      return timeConverter(this.filteredTicketInfo.lottery_result_time_at).shortDateWithWeekDayAndTime
    },
    eventOnlineAreaName() {
      if (this.event.all_optional_prefectures_selected) return 'オンライン'
      return `オンライン ${this.event.prefecture_name}その他`
    },
    allTicketsSoldOut() {
      if (!this.filteredTicketInfo || !this.event) return true

      const tickets = this.filteredTicketInfo.tickets ?? []
      if (!tickets.length) return true

      const userGender = this.$store.myGender || this.$store.$state.my_profile?.gender
      const applicableTickets =
        this.$store.isLoggedIn && userGender ? tickets.filter(ticket => ticket.gender === userGender || ticket.gender === 'both_gender') : tickets

      if (!applicableTickets.length) return true

      // Use comprehensive sold-out validation for applicable tickets only
      return applicableTickets.every(ticket => shouldDisablePurchaseButton(ticket, this.event))
    }
  },
  methods: {
    getTicketInfo() {
      if (!this.pageDetector.isEventPreviewPage) {
        this.$store.dispatch('apiGet', `events/${this.$route.params.code}/me`).then(res => {
          this.ticketInfo = res.data
          this.loadedLatestTicketInfo = true
        })
      } else {
        this.loadedLatestTicketInfo = true
      }
    },
    onTicketRowClick() {
      // Pattern B: Do nothing when clicking ticket row
      if (this.abGroup === 'B') {
        return
      }
      // Pattern A: Same behavior as purchase button
      this.onPurchaseButton()
    },
    async onPurchaseButton() {
      // Pattern B: Check AB group first
      if (this.abGroup === 'B') {
        const eventCode = this.eventCode
        if (this.$store.isLoggedIn) {
          // For logged-in users: Fetch real ticket info, auto-select by gender, redirect to Pattern A page
          try {
            const res = await this.$store.dispatch('apiGet', `events/${eventCode}/me`)
            const tickets = res.data.tickets || []
            const userGender = this.$store.myGender || this.$store.$state.my_profile?.gender
            const selectedTicket = tickets.find(t => t.gender === userGender || t.gender === 'both_gender')

            if (selectedTicket) {
              this.$router.push(`/events/${eventCode}/tickets/${selectedTicket.id}/purchases/new?quantity=1`)
            } else {
              console.warn(`No ticket available for gender: ${userGender}`)
            }
          } catch (error) {
            console.error('Failed to fetch ticket info:', error)
          }
        } else {
          // For guest users: Navigate to new Pattern B page with mock data
          this.$router.push(`/events/${eventCode}/guest_purchases/new?quantity=1`)
        }
        return
      }

      // Pattern A: Original behavior
      if (this.ticketInfo.purchased_online_event_ticket_redirect_path)
        return this.$router.push(this.ticketInfo.purchased_online_event_ticket_redirect_path)
      if (!this.ticketInfo) return
      const payloadData = {
        ...this.filteredTicketInfo,
        event_id: this.event.id,
        event_title: this.event.title,
        event_code: this.eventCode,
        event_type: this.event.type,
        goto_campaign_event: this.event.goto_campaign_event,
        is_five_minutes_event: this.isFiveMinutesEvent,
        event_start_at: this.event.start_at,
        event_sale_end_at: this.event.sale_end_at,
        event_end_at: this.event.end_at,
        event_cancel_prices: this.event.cancel_prices,
        male_age_range: { from: this.event.male_age_lower, to: this.event.male_age_upper },
        female_age_range: { from: this.event.female_age_lower, to: this.event.female_age_upper },
        lottery_deadline_at: this.lotteryDeadlineAt,
        lottery_result_time_at: this.lotteryResultTimeAt,
        has_ticket_set_sale: this.event.has_ticket_set_sale,
        ticket_set_sale_settings: this.event.ticket_set_sale_settings
      }
      this.pushGtmCheckoutData()
      this.$store.MODAL({ name: 'PurchaseModal', payload: payloadData })
      if (this.$store.isLoggedIn) this.$store.dispatch('createUserClickPurchaseEvent', { event_id: this.event.id })
    },
    pushGtmCheckoutData() {
      GTM.sendEcommerceDataToGa(GA4_BEGIN_CHECKOUT, this.event, this.$store.myGender)
    }
  }
}
